import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Link2 } from "lucide-react";
import { Button, Spinner } from "./ui/kit";
import { Dialog } from "./SkladDialogy";
import CenikPicker from "./CenikPicker";
import { fmtKc } from "../lib/format";
import api, { apiError } from "../lib/api";

/**
 * Propojení skladu s ceníkem. Ceník je nadřazený: propojená skladová položka
 * bere nákupní cenu z ceníku (hodnota zásob, výdejky). Server navrhne shodu
 * podle kódu, EAN a názvu; co v ceníku není, jde rovnou založit.
 */
const VOLBY = [["PROPOJIT", "Propojit"], ["ZALOZIT", "Založit v ceníku"], ["NIC", "Přeskočit"]];
const SHODA = { EXACT: "shoda kódu", FUZZY: "shoda názvu" };

export default function ParovaniCeniku({ onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [volba, setVolba] = useState({});      // id skladové položky → PROPOJIT | ZALOZIT | NIC
  const [jina, setJina] = useState({});        // ručně vybraná položka ceníku
  const [picker, setPicker] = useState(null);  // id skladové položky, pro kterou se vybírá
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/api/sklad/parovani-ceniku").then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e.response?.data?.detail) || e.message));
  }, []);

  const cil = (p) => jina[p.id] || p.navrh;
  const volbaPro = (p) => volba[p.id] || (cil(p) ? "PROPOJIT" : "ZALOZIT");
  const pocty = useMemo(() => (data?.items || []).reduce((a, p) => {
    a[volbaPro(p)] += 1; return a;
  }, { PROPOJIT: 0, ZALOZIT: 0, NIC: 0 }), [data, volba, jina]); // eslint-disable-line react-hooks/exhaustive-deps

  const ulozit = async () => {
    setSaving(true); setErr("");
    const pary = data.items.map((p) => {
      const v = volbaPro(p);
      if (v === "PROPOJIT" && cil(p)) return { sklad_polozka_id: p.id, polozka_id: cil(p).id };
      if (v === "ZALOZIT") return { sklad_polozka_id: p.id, zalozit: true };
      return null;
    }).filter(Boolean);
    try {
      await api.post("/api/sklad/parovani-ceniku", { pary });
      onSaved?.(); onClose();
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
      setSaving(false);
    }
  };

  const ulozitText = [pocty.PROPOJIT && `propojit ${pocty.PROPOJIT}`, pocty.ZALOZIT && `založit ${pocty.ZALOZIT}`]
    .filter(Boolean).join(" · ");

  return (
    <>
      <Dialog title="Propojit sklad s ceníkem" bare sirka="md:max-w-[760px]" onClose={onClose} testid="parovani-ceniku"
        meta={data ? `${data.items.length} položek skladu není v ceníku · propojeno ${data.propojeno} z ${data.celkem}` : ""}
        footer={<>
          <Button variant="ghost" onClick={onClose} className="hidden md:inline-flex">Zrušit</Button>
          <Button className="flex-1 md:flex-none md:ml-auto !h-12 md:!h-10" onClick={ulozit}
            disabled={saving || !data || !ulozitText} data-testid="parovani-ulozit">
            {saving ? <Spinner /> : ulozitText ? `Uložit — ${ulozitText}` : "Není co uložit"}
          </Button>
        </>}>
        <div className="px-4 md:px-5 py-3 t-meta text-sec bg-inputbg border-b border-line-softer flex items-start gap-2">
          <Link2 size={14} className="mt-0.5 shrink-0 text-accent" />
          <span>Propojená položka bere nákupní cenu z ceníku — hodnota zásob i výdejky na zakázky půjdou cenou z ceníku.
            Co v ceníku není, se tam založí s cenou ze skladu.</span>
        </div>
        {data === null ? (
          err ? <div className="p-5 t-meta text-danger">{err}</div> : <div className="p-10 flex justify-center"><Spinner /></div>
        ) : !data.items.length ? (
          <div className="p-6 t-telo text-sec" data-testid="parovani-hotovo">Všechny skladové položky jsou propojené s ceníkem.</div>
        ) : data.items.map((p) => {
          const c = cil(p);
          const v = volbaPro(p);
          return (
            <div key={p.id} className="px-4 md:px-5 py-3 border-b border-line-softer last:border-0 flex flex-col md:flex-row md:items-center gap-2 md:gap-3"
              data-testid={`parovani-${p.id}`}>
              <div className="min-w-0 md:w-[34%]">
                <div className="t-telo font-600 text-ink truncate">{p.nazev}</div>
                <div className="t-meta text-muted amount truncate">{[p.kod || p.ean, `sklad ${fmtKc(p.cena_nakup)} Kč/${p.mj}`].filter(Boolean).join(" · ")}</div>
              </div>
              <ArrowRight size={15} className="hidden md:block text-muted-2 shrink-0" />
              <div className="min-w-0 flex-1">
                {v === "ZALOZIT" ? (
                  <div className="t-meta text-sec">Nová položka ceníku „{p.nazev}“ za {fmtKc(p.cena_nakup)} Kč</div>
                ) : c ? (
                  <>
                    <div className="t-telo font-600 text-ink truncate">{c.name}</div>
                    <div className="t-meta text-muted amount truncate">
                      {[c.katalogove_cislo, `ceník ${fmtKc(c.cena_nakup)} Kč/${c.mj}`, jina[p.id] ? "vybráno ručně" : SHODA[p.match],
                        c.mj && p.mj && c.mj !== p.mj ? `pozor: jiná jednotka (${c.mj})` : null].filter(Boolean).join(" · ")}
                    </div>
                    {c.obsazeno && <div className="t-meta text-accent-hover">Už ji má skladová položka „{c.obsazeno}“</div>}
                  </>
                ) : <div className="t-meta text-muted">V ceníku jsme ji nenašli.</div>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                <div className="inline-flex p-0.5 rounded-pill bg-line-soft">
                  {VOLBY.filter(([k]) => k !== "PROPOJIT" || c).map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setVolba((o) => ({ ...o, [p.id]: k }))}
                      data-testid={`parovani-${p.id}-${k}`}
                      className={`tap md:min-h-0 h-9 md:h-7 px-3 rounded-pill t-meta font-600 ${v === k ? "bg-ink text-white" : "text-sec hover:text-ink"}`}>{l}</button>
                  ))}
                </div>
                <button type="button" onClick={() => setPicker(p.id)} data-testid={`parovani-${p.id}-jina`}
                  className="tap md:min-h-0 h-9 md:h-7 px-2 t-meta font-700 text-accent-hover">Jiná…</button>
              </div>
            </div>
          );
        })}
        {err && data && <div className="px-5 py-3 t-meta text-danger">{err}</div>}
      </Dialog>
      {picker && (
        <CenikPicker onClose={() => setPicker(null)}
          onAdd={(c) => { setJina((o) => ({ ...o, [picker]: c })); setVolba((o) => ({ ...o, [picker]: "PROPOJIT" })); setPicker(null); }} />
      )}
    </>
  );
}
